import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, CheckSquare, Clock, AlertCircle, X, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPTasks = () => {
  const { getAuthHeader, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_id: '',
    assigned_to: '',
    priority: 'medium',
    due_date: '',
    category: 'execution'
  });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, myTasksRes, statsRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/tasks/`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/tasks/my-tasks`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/tasks/stats/overview`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/projects`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/erp/communication/chat/users`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setTasks(tasksRes.data);
      setMyTasks(myTasksRes.data);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, [BACKEND_URL, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await axios.put(
          `${BACKEND_URL}/api/erp/tasks/${editingTask.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/erp/tasks/`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      fetchData();
      setShowModal(false);
      setFormData({ title: '', description: '', project_id: '', assigned_to: '', priority: 'medium', due_date: '', category: 'execution' });
      setEditingTask(null);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const updateStatus = async (taskId, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/erp/tasks/${taskId}`,
        { status },
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description,
      project_id: task.project_id || '',
      assigned_to: task.assigned_to,
      priority: task.priority,
      due_date: task.due_date.split('T')[0],
      category: task.category
    });
    setShowModal(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/erp/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const displayTasks = activeTab === 'my' ? myTasks : tasks;
  const pendingTasks = displayTasks.filter(t => t.status === 'pending');
  const inProgressTasks = displayTasks.filter(t => t.status === 'in_progress');
  const completedTasks = displayTasks.filter(t => t.status === 'completed');

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const TaskCard = ({ task }) => {
    const isOverdue = new Date(task.due_date) < new Date() && task.status !== 'completed';
    
    return (
      <Card className={`p-4 ${isOverdue ? 'border-l-4 border-red-500' : ''}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold">{task.title}</h3>
              <span className={`text-xs px-2 py-0.5 rounded ${getPriorityColor(task.priority)}`}>
                {task.priority}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{task.description}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {new Date(task.due_date).toLocaleDateString()}
              </span>
              <span>{task.category}</span>
              <span>Assigned to: {task.assigned_to}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {task.status === 'pending' && (
              <Button size="sm" onClick={() => updateStatus(task.id, 'in_progress')} className="bg-blue-600">
                Start
              </Button>
            )}
            {task.status === 'in_progress' && (
              <Button size="sm" onClick={() => updateStatus(task.id, 'completed')} className="bg-green-600">
                Complete
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => handleEdit(task)}>
              Edit
            </Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(task.id)}>
              Delete
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
          <p className="text-gray-600 mt-1">Track and manage project tasks</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-primary">
          <Plus className="w-4 h-4 mr-2" />
          Create Task
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Tasks</p><p className="text-2xl font-bold">{stats?.total_tasks || 0}</p></div><CheckSquare className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">In Progress</p><p className="text-2xl font-bold text-blue-600">{stats?.in_progress || 0}</p></div><Clock className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Completed</p><p className="text-2xl font-bold text-green-600">{stats?.completed || 0}</p></div><CheckSquare className="w-10 h-10 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Overdue</p><p className="text-2xl font-bold text-red-600">{stats?.overdue || 0}</p></div><AlertCircle className="w-10 h-10 text-red-500" /></div></CardContent></Card>
      </div>

      <div className="mb-6 border-b">
        <div className="flex gap-4">
          <button onClick={() => setActiveTab('all')} className={`pb-3 px-4 font-medium ${activeTab === 'all' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>All Tasks</button>
          <button onClick={() => setActiveTab('my')} className={`pb-3 px-4 font-medium ${activeTab === 'my' ? 'border-b-2 border-primary text-primary' : 'text-gray-600'}`}>My Tasks</button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            Pending ({pendingTasks.length})
          </h2>
          <div className="space-y-3">
            {pendingTasks.map(task => <TaskCard key={task.id} task={task} />)}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            In Progress ({inProgressTasks.length})
          </h2>
          <div className="space-y-3">
            {inProgressTasks.map(task => <TaskCard key={task.id} task={task} />)}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            Completed ({completedTasks.length})
          </h2>
          <div className="space-y-3">
            {completedTasks.map(task => <TaskCard key={task.id} task={task} />)}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingTask ? 'Edit Task' : 'Create Task'}</h2>
              <Button size="sm" variant="ghost" onClick={() => { setShowModal(false); setEditingTask(null); }}>
                <X size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows="3" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Assign To</label>
                  <select value={formData.assigned_to} onChange={(e) => setFormData({...formData, assigned_to: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required>
                    <option value="">Select User</option>
                    {users.map(u => <option key={u.email} value={u.email}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project (Optional)</label>
                  <select value={formData.project_id} onChange={(e) => setFormData({...formData, project_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.project_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="planning">Planning</option>
                    <option value="execution">Execution</option>
                    <option value="review">Review</option>
                    <option value="documentation">Documentation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Due Date</label>
                  <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingTask(null); }}>Cancel</Button>
                <Button type="submit" className="bg-primary">{editingTask ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ERPTasks;